import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilsService } from '../../../shared/utils.service';
import { ApiRequestService } from '../../../shared/api-request.service';
import { UserPublicProfileComponent } from '../../../shared/user-public-profile/user-public-profile.component';
import { AssetMaintenanceScheduleModel } from '../../../models/asset-maintenance-schedule.model';
import { AssetMaintenanceRecordsService } from '../../asset-maintenance-records/asset-maintenance-records.service';
import { AssetMaintenanceSchedulesService } from '../asset-maintenance-schedules.service';
import { AppService } from '../../../app.service';
import { SchedulerFormValue, SchedulerValue } from '../../../shared/scheduler/scheduler.interface';
import { SchedulerService } from '../../../shared/scheduler/scheduler.service';
import { RBACService } from '../../../shared/rbac.service';
import { assetUserCanAccess } from '../../assets-permissions';

@Component({
  selector: 'app-asset-maintenance-schedules-view',
  templateUrl: './asset-maintenance-schedules-view.component.html',
  styleUrls: ['./asset-maintenance-schedules-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetMaintenanceSchedulesViewComponent implements OnInit {
  asset_maintenance_schedule: AssetMaintenanceScheduleModel = new AssetMaintenanceScheduleModel();

  // RBAC permission flag: resolved once in ngOnInit(). Maintenance schedules belong to an asset, so
  // viewing one needs the asset view permission in either perspective (account or site).
  canView: boolean = false;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public assetMaintenanceRecordsService: AssetMaintenanceRecordsService,
    public assetMaintenanceSchedulesService: AssetMaintenanceSchedulesService,
    public schedulerService: SchedulerService,
    public rbac: RBACService,
    private dialogRef: MatDialogRef<AssetMaintenanceSchedulesViewComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: any
  ) {}

  ngOnInit() {
    const asset_id = this.dialogData?.asset_id;
    const asset_maintenance_schedule_id = this.dialogData?.asset_maintenance_schedule_id;

    this.canView = assetUserCanAccess(this.rbac, RBACService.RBAC_A.A_VIEW);

    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view maintenance schedules.');
      this.dialogRef.close();
      return;
    }

    if (typeof asset_id === 'number' && asset_id > 0) {
      this.asset_maintenance_schedule.asset_id = asset_id;
    }

    if (typeof asset_maintenance_schedule_id === 'number' && asset_maintenance_schedule_id > 0) {
      this.find(asset_maintenance_schedule_id);
    }
  }

  /**
   * Retrieves the Maintenance Schedule with the specified ID.
   *
   * @param {number} id - The unique identifier of the maintenance schedule to retrieve.
   * @return {void}
   */
  private find(id: number): void {
    this.api
      .makeRequest('get', `v2/assets/${this.asset_maintenance_schedule.asset_id}/maintenance-schedules/${id}`)
      .then((response) => {
        this.asset_maintenance_schedule = response.data as AssetMaintenanceScheduleModel;
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Determines if the current schedule is recurring based on the asset maintenance schedule's properties.
   *
   * @return {boolean} Returns true if the schedule is recurring, otherwise false.
   */
  get isRecurringSchedule(): boolean {
    return !!(
      this.asset_maintenance_schedule.is_recurring &&
      this.asset_maintenance_schedule.rrule &&
      this.asset_maintenance_schedule.schedule_start_at
    );
  }

  /**
   * Retrieves a summary of the duration for the maintenance schedule.
   *
   * @return {string} The formatted duration summary based on the schedule's duration in minutes.
   */
  get durationSummary(): string {
    return this.schedulerService.getDurationSummary(this.asset_maintenance_schedule.duration_minutes);
  }

  /**
   * Generates and returns a summary of the asset maintenance schedule.
   *
   * The summary includes details based on recurrence and whether the event covers the entire day.
   * If the schedule is non-recurring or lacks sufficient data, an empty string is returned.
   *
   * @return {string} A formatted summary of the asset maintenance schedule or an empty string if the schedule is invalid.
   */
  get scheduleSummary(): string {
    if (
      !this.asset_maintenance_schedule.is_recurring ||
      !this.asset_maintenance_schedule.rrule ||
      !this.asset_maintenance_schedule.schedule_start_at
    ) {
      return '';
    }

    const schedulerValue: SchedulerValue = {
      is_all_day: !!this.asset_maintenance_schedule.is_all_day,
      duration_minutes: this.asset_maintenance_schedule.duration_minutes,
      rrule: this.asset_maintenance_schedule.rrule,
      schedule_start_at: this.asset_maintenance_schedule.schedule_start_at,
      schedule_end_at: this.asset_maintenance_schedule.schedule_end_at
    };

    const form: SchedulerFormValue = this.schedulerService.toSchedulerFormValue(schedulerValue);

    const timezone = this.app.account.timezone;

    return this.schedulerService.getSummary(form, timezone);
  }

  /**
   * Open the User's public profile dialog.
   *
   * @param hash
   * @return {void}
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
      }
    );
  }
}
